<template>
  <div>
    <ServicesHeroPortal
      v-show="!isAuthenticated"
      :title="titlePage"
      :icon="general.icon"
      :sub-title="general.subTitle"
      :list-items="general.listItems"
      :images="general.images"
      :services="services"
    />
    <div
      class="mx-auto flex w-full max-w-content-container flex-col justify-center gap-y-7 px-small pb-20 md:items-start xl:pb-36 xl:pl-16 xl:pr-0"
      :class="{
        'pt-8 xl:pt-10': isAuthenticated,
      }"
    >
      <div v-show="isAuthenticated">
        <Breadcrumb :menu="breadcrumbStore.items" />
      </div>

      <div class="relative h-full w-full">
        <SectionTitle
          v-show="isAuthenticated"
          data-testid="portal-title"
          class="mb-6 ml-0 lg:ml-5"
          :title="titlePage"
          :icon="general.icon"
          :mineral="general.category"
          authorized
        />

        <DailyIndicator
          v-if="isLithium && isAuthenticated"
          :ref="`daily-price-${jwtToken}`"
        />
        <div class="mt-5 pb-0 lg:mt-8">
          <div class="flex grid-cols-5 flex-col gap-x-3 gap-y-6 xl:grid">
            <div
              v-if="!hideTableAndChart"
              class="col-span-3 rounded-xl bg-s-300"
              :class="loadingPricesPortal ? 'animate-pulse' : ''"
            >
              <PortalLithiumGlobalPrice
                v-show="!loadingPricesPortal"
                :category="general.category"
                :slug="general.alternativeSlug ?? general.slug"
                :title-prices-summary="titlePricesTable"
                :prices-summary-portal="pricesSummaryPortal"
                :title-prices="titlePrices"
                :prices-portal="pricesPortal"
                :loading-prices-portal="loadingPricesPortal"
                :has-marketing-page="general.hasMarketingPage"
              />
            </div>
            <div
              class="flex flex-col gap-2.5"
              :class="
                hideTableAndChart
                  ? 'col-span-5 grid-cols-2 xl:grid'
                  : 'col-span-2'
              "
            >
              <div
                class="flex min-h-[220px] w-full animate-pulse items-center justify-center rounded-xl bg-s-300 xl:min-h-[381px]"
                :class="{
                  '!animate-none': !loadingArticles,
                  'xl:!min-h-[274px]': hideTableAndChart,
                }"
              >
                <CarouselArticle
                  v-if="articles.length"
                  :market="general.slug"
                  :articles="articles"
                  class="w-full"
                  :class="hideTableAndChart ? 'xl:h-[274px]' : 'xl:h-[381px]'"
                />
              </div>
              <div
                class="relative h-[181px] grow rounded-xl bg-s-300 md:h-60 xl:h-auto"
              >
                <CarouselCapexTracker
                  class="!relative rounded-xl [&_img]:rounded-xl"
                  button-classes="!flex bg-white !text-black border-2"
                  :material="ALL_MATERIALS_TOTALS.mineral"
                  :value="ALL_MATERIALS_TOTALS.value"
                  :image-url="ALL_MATERIALS_TOTALS.imageUrl"
                />
              </div>
            </div>
          </div>

          <div class="relative mt-5 w-full max-w-content-container">
            <div class="mb-[58px] mt-16 w-full">
              <div
                v-if="loadingReports"
                class="flex h-[444px] w-full items-center justify-center"
              >
                <TheLoader />
              </div>
              <ReportsAndModelsSection
                v-else
                :key="`reports-and-models-${jwtToken}`"
                :reports="reportCards"
                :product="product"
                :category="category"
              />
            </div>
          </div>

          <div class="relative mt-5 w-full max-w-content-container">
            <VideoSection
              :key="`video-section-${jwtToken}`"
              :product-slug="general.slug"
              :videos="videos"
            />
          </div>

          <div
            v-if="!hideSustainabilityIndex"
            class="mt-8 w-full max-w-content-container rounded-xl bg-white p-6 shadow-md"
          >
            <SustainabilityIndexSection
              :key="`esg-${jwtToken}`"
              :sustainability-indexes="esgData.sustainabilityIndexes || []"
              :slug="sustainabilityIndexSlug"
              :checkbox="esgData.checkbox"
            />
          </div>
          <div class="mt-8 w-full max-w-content-container">
            <EventSection />
          </div>
        </div>
      </div>
    </div>
    <PdfViewerModal :url="pdfUrl" @close="closeViewer" />
  </div>
</template>

<script setup lang="ts">
import CarouselCapexTracker from '~/components/carousel/CapexTracker.vue'
import PdfViewerModal from '~/components/critical-insights/PdfViewerModal.vue'
import CarouselArticle from '~/components/market-insights/CarouselArticle.vue'
import DailyIndicator from '~/components/portals/DailyIndicator/index.vue'
import EventSection from '~/components/portals/EventSection.vue'
import ReportsAndModelsSection from '~/components/portals/ReportsAndModelsSection.vue'
import SustainabilityIndexSection from '~/components/portals/SustainabilityIndexSection.vue'
import VideoSection from '~/components/portals/VideoSection.vue'
import PortalLithiumGlobalPrice from '~/components/price-assessments/PortalLithiumGlobalPrice.vue'
import SectionTitle from '~/components/price-assessments/SectionTitle.vue'
import Breadcrumb from '~/components/shared/navigation/Breadcrumb.vue'
import ServicesHeroPortal from '~/components/shared/services/HeroPortal.vue'
import useAuthentication from '~/composables/useAuthentication'
import { useReports } from '~/composables/useReports'
import { ALL_MATERIALS_TOTALS } from '~/constants/capex-tracker-data'
import { getServices } from '~/constants/services'
import { LITHIUM } from '~/constants/services/constants'
import { MARKET_SLUG, MARKET_TYPE, MARKET_TYPES } from '~/src/constants/markets'
import type { TEvent } from '~/src/types/events'
import type { TMarketSlug } from '~/src/types/markets'
import type { TReportCard } from '~/src/types/reports'
import useAuthStore from '~/stores/auth'
import { useBreadcrumbStore } from '~/stores/breadcrumb'
import { useModalStore } from '~/stores/modal'
import {
  getAdaptedSummaryTable,
  transformIndexToPricesStructure,
} from '~/utils/adapters'
import { reformat } from '~/utils/date'
import { logError } from '~/utils/log-error'

interface Props {
  general?: {
    name: string
    slug: TMarketSlug
    icon: string
    subTitle: string
    listItems: string[]
    images: {
      desktop: string
      mobile: string
    }
    title: string
    category: string
    categoriesArticles?: string[]
    hideTableAndChart?: boolean
    hideSustainabilityIndex?: boolean
    reportSlug?: string
    categoryReportPricing: string
    menu: string
    alternativeSlug?: string
    hasMarketingPage?: boolean
  }
  esgData?: {
    sustainabilityIndexes?: any[]
    checkbox?: string
  }
}

const props = withDefaults(defineProps<Props>(), {
  general: () => null,
  esgData: () => ({
    sustainabilityIndexes: [],
    checkbox: '',
  }),
})

const { $graphqlClient, $restClient } = useNuxtApp()
const { getReportAndModelData } = useReports()
const authStore = useAuthStore()
const breadcrumbStore = useBreadcrumbStore()
const modalStore = useModalStore()
const { getVideos, getEvents } = usePortal()
const { modalParams } = storeToRefs(modalStore)

const pricesPortal = ref([])
const loadingPricesPortal = ref(true)
const pricesSummaryPortal = ref([])
const articles = ref([])
const loadingArticles = ref(true)
const isPrices = ref(true)
const hideTableAndChart = ref(false)
const hideSustainabilityIndex = ref(false)
const services = ref({ checkboxes: [] })
const reportCards = ref<TReportCard[]>([])
const loadingReports = ref(true)
const videos = ref([])
const events = ref<TEvent[]>([])

const pdfUrl = computed(() => modalParams.value?.pdfUrl)
const categoriesArticles = computed(
  () => props.general.categoriesArticles ?? [props.general.slug],
)
const product = computed(() => props.general.reportSlug || props.general.slug)
const category = computed(
  () => props.general.categoryReportPricing ?? product.value,
)
const isAuthenticated = computed(() => authStore.authenticated)
const isRecycling = computed(() => props.general.slug === MARKET_SLUG.RECYCLING)
const jwtToken = computed(() =>
  authStore.jwtToken ? 'with-token' : 'without-token',
)
const titlePage = computed(() => `${props.general.title}`)
const isLithium = computed(() => props.general.slug === LITHIUM.slug)

const sustainabilityIndexSlug = computed(() => {
  const graphiteSlugs = ['natural-graphite', 'synthetic-graphite']
  const isGraphite = graphiteSlugs.includes(props.general.slug)
  return isGraphite ? 'graphite' : props.general.slug
})

const titlePrices = computed(() => {
  if (isPrices.value) return `${props.general.name} Global Weighted Average`
  if (isRecycling.value) return `${props.general.menu} Index`
  return `${props.general.name} Index`
})

const titlePricesTable = computed(() => {
  if (isRecycling.value) return `${props.general.menu}`
  return `${props.general.title}`
})

const closeViewer = () => {
  modalStore.setModalParams({ pdfUrl: '' })
}

const getCriticalInsights = async () => {
  const reports = await getReportAndModelData(
    props.general.slug,
    [MARKET_TYPE.CRITICAL_INSIGHTS],
    true,
    1,
  )
  return reports.map((report) => ({
    ...report,
    backgroundImage: report.images.desktop,
    publishedDate: reformat(report.date, 'dd MMM yyyy'),
  }))
}

const getArticles = async () => {
  loadingArticles.value = true
  try {
    const [portalArticles, criticalInsights] = await Promise.all([
      $graphqlClient.getPortalArticles({
        market: props.general.slug,
        categories: props.general?.categoriesArticles || null,
      }),
      getCriticalInsights(),
    ])
    articles.value = portalArticles.articles
      .toSpliced(2, 0, ...criticalInsights)
      .slice(0, 5)
  } catch (error) {
    logError(error)
  } finally {
    loadingArticles.value = false
  }
}

const getPricesPortal = async () => {
  try {
    const response = await $restClient.getPricesPortalByMineral(
      props.general.category,
    )
    if (response.prices) {
      pricesPortal.value = response.prices.chart
      pricesSummaryPortal.value = getAdaptedSummaryTable({
        summaries: response.prices.table,
      })
    } else if (response.indexes) {
      isPrices.value = false
      pricesPortal.value = response.indexes.detail.map((item) => ({
        category: {
          name: item.product,
        },
        currency: {
          symbol: '',
        },
        series: item.data.map((seriesData) => ({
          valueMid: seriesData.value,
          date: seriesData.date,
        })),
      }))

      pricesSummaryPortal.value = [transformIndexToPricesStructure(response)]
    } else {
      hideTableAndChart.value = true
    }
  } catch (error) {
    logError(error)
    hideTableAndChart.value = true
  } finally {
    loadingPricesPortal.value = false
  }
}

const fetchReports = async () => {
  loadingReports.value = true
  const marketTypes = MARKET_TYPES.filter(
    (type) => type !== MARKET_TYPE.CRITICAL_INSIGHTS,
  )
  try {
    const marketSlug = props.general.slug
    reportCards.value = await getReportAndModelData(marketSlug, marketTypes)
    loadingReports.value = false
  } catch (error) {
    logError(error)
  } finally {
    loadingReports.value = false
  }
}

onMounted(async () => {
  hideTableAndChart.value = props.general.hideTableAndChart || false
  hideSustainabilityIndex.value = props.general.hideSustainabilityIndex || false
  services.value = getServices([props.general.slug])
  try {
    const response = await Promise.all([
      fetchReports(),
      getArticles(),
      !hideTableAndChart.value && getPricesPortal(),
      getVideos({
        categories: categoriesArticles.value,
        slug: props.general.slug,
      }),
      getEvents(),
    ])

    videos.value = response[3] || []
    events.value = response[4] || []
  } catch (error) {
    logError(error)
  }
})

useAuthentication(async () => {
  await Promise.all([getArticles(), fetchReports()])
})
</script>
